import type { AlertAssignment, AlertRuleState, AlertTemplate } from "./AlertRuleCard"
import { appliesToClass } from "./alertRuleFormat"

/** An Asset, Module or System that alerts can be assigned to. */
export interface AlertTarget {
  id: string
  class?: string | null
}

/**
 * The rules an alert configuration page shows, so every page counts the same way: each
 * Standard Alert once per target whose Asset Class it applies to, and each Custom Alert
 * owned by a target on the Asset its one assignment judges. A Custom Alert with no
 * assignment has lost its Asset and is left out.
 */
export function buildAlertRules(
  templates: AlertTemplate[],
  assignments: AlertAssignment[],
  targets: AlertTarget[],
): AlertRuleState[] {
  const byTarget = new Map<string, AlertAssignment>()
  const byTemplate = new Map<string, AlertAssignment>()
  for (const a of assignments) {
    byTarget.set(`${a.templateId}:${a.assetId ?? a.systemId ?? ""}`, a)
    byTemplate.set(a.templateId, a)
  }
  const targetIds = new Set(targets.map((t) => t.id))

  const rule = (template: AlertTemplate, assignment: AlertAssignment | null, targetAssetId: string) => ({
    template,
    assignment,
    isEnabled: assignment?.enabled ?? false,
    isOverridden: assignment?.thresholdOverride != null || assignment?.timeWindowOverrideSeconds != null,
    effectiveThreshold: assignment?.thresholdOverride ?? template.threshold,
    targetAssetId,
  })

  return templates.flatMap((template) => {
    if (template.origin?.toUpperCase() === "CUSTOM") {
      const assignment = byTemplate.get(template.id)
      if (!assignment || !targetIds.has(template.ownerAssetId ?? "")) return []
      return [rule(template, assignment, assignment.assetId ?? template.ownerAssetId ?? "")]
    }
    return targets
      .filter((target) => appliesToClass(template.assetClasses, target.class))
      .map((target) => rule(template, byTarget.get(`${template.id}:${target.id}`) ?? null, target.id))
  })
}
